import {utc} from 'moment'

import {parseDateToObj} from '../../../../utils/dateTimeUtils'
import type {BuildLogMessage, BuildLogMessageSelectionStart} from '../BuildLog.types'
import {ansiRegexpString} from '../BuildLog.utils'

import styles from './BuildLogMessages.css'

export const ROW_HEIGHT = 18
const UNIT = 8
const ROW_PADDING = 98
const EXTRA_ROW_PADDING = ROW_PADDING + 2 * UNIT
const DEFAULT_UNCALCULATED_CHAR_WIDTH = 7.25
let cachedCharWidth: number | null | undefined = null

const getCharWidth = (className: string): number => {
  if (cachedCharWidth != null) {
    return cachedCharWidth
  }

  const testString = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/-.= ()"'
  const span = document.createElement('span')
  span.innerHTML = testString
  const div = document.createElement('div')
  div.className += className
  div.style.visibility = 'none'
  div.style.opacity = '0'
  div.style.position = 'absolute'
  div.style.left = '-1000px'
  div.style.top = '-1000px'
  div.appendChild(span)

  if (document.body != null) {
    document.body.appendChild(div)
  }

  const round = 100
  let result = Math.ceil((span.getBoundingClientRect().width / testString.length) * round) / round

  if (result === 0) {
    result = DEFAULT_UNCALCULATED_CHAR_WIDTH
  }

  if (document.body != null) {
    document.body.removeChild(div)
  }

  cachedCharWidth = result
  return result
}

export const calculateMessageHeight = (
  message: BuildLogMessage | null | undefined,
  listWidth: number | null | undefined,
  last: boolean,
  softWrapLines: boolean,
  extraPadding?: boolean,
): number => {
  const lastPadding = last ? 2 * ROW_HEIGHT : 0

  if (message?.linesLengths == null) {
    return ROW_HEIGHT + lastPadding
  }

  let linesCount = message.linesLengths.length

  if (softWrapLines && listWidth != null && listWidth > 0) {
    const charWidth = getCharWidth(styles.messages)
    const charsPerLineCount = Math.floor(
      (listWidth - (extraPadding ? EXTRA_ROW_PADDING : ROW_PADDING)) / charWidth,
    )

    if (message.linesLengths != null) {
      linesCount = message.linesLengths.reduce(
        (acc: number, lineLength: number) =>
          acc + (lineLength > 0 ? Math.ceil(lineLength / charsPerLineCount) : 1),
        0,
      )
    }
  }

  return linesCount * ROW_HEIGHT + lastPadding
}
export const calculateTotalMessagesHeight = (
  messages: ReadonlyArray<BuildLogMessage | null | undefined>,
  listWidth: number,
  softWrapLines: boolean,
  extraPadding: boolean,
): number =>
  messages.reduce(
    (acc: number, message: BuildLogMessage | null | undefined, index: number) =>
      acc +
      calculateMessageHeight(
        message,
        listWidth,
        index === messages.length - 1,
        softWrapLines,
        extraPadding,
      ),
    0,
  )
export const getMessageWidth = (length: number, extraPadding: boolean): number =>
  length * getCharWidth(styles.messages) +
  (extraPadding ? EXTRA_ROW_PADDING : ROW_PADDING) +
  2 * UNIT

export const leftPad = (level: number): string => Array(level).join('\u00a0\u00a0')

export const getTextWithSpaceIndents = (text: string, spaces: string): string =>
  text
    .split('\n')
    .map(line => `${spaces}${line}`)
    .join('\n')

export const printMessageTime = (timestamp: string | undefined, isAutodetectTimeZone: boolean) =>
  timestamp != null
    ? `${parseDateToObj(timestamp, isAutodetectTimeZone).format('HH:mm:ss')}\u00a0`
    : ''

export const printRelativeTime = (
  timestamp: string | undefined,
  buildStart: string | undefined,
  isAutodetectTimeZone: boolean,
) => {
  if (!timestamp || !buildStart) {
    return '00:00:00'
  }
  const currentDate = parseDateToObj(timestamp, isAutodetectTimeZone)
  const buildStartDate = parseDateToObj(buildStart, isAutodetectTimeZone)
  const diff = currentDate.diff(buildStartDate)
  return `${utc(diff < 0 ? 0 : diff).format('HH:mm:ss')}\u00a0`
}

const getDataId = (el: HTMLElement): number | null => {
  const id = el.getAttribute('data-id')
  return id != null ? parseInt(id, 10) : null
}

const getRowDataIdFromParents = (node: Node): number | null => {
  let parent = node.parentElement
  while (parent != null) {
    if (parent.hasAttribute('data-log-row')) {
      return getDataId(parent)
    }
    parent = parent.parentElement
  }
  return null
}

const getMessageSelectionPosition = (
  selection: Selection,
  direction: 'down' | 'up',
): {
  index: number
  messageId: number | null
  timeSelected: boolean
} | null => {
  let node
  let offset
  if (direction === 'down') {
    const range = selection.getRangeAt(0)
    node = range.startContainer
    offset = range.startOffset
  } else {
    const range = selection.getRangeAt(selection.rangeCount - 1)
    node = range.endContainer
    offset = range.endOffset
  }

  if (node == null) {
    return null
  }
  if (
    (node.nodeType === Node.TEXT_NODE &&
      node.parentElement?.parentElement?.parentElement?.hasAttribute('data-log-message-time')) ||
    (node instanceof HTMLElement && node.hasAttribute('data-log-message-time'))
  ) {
    return {
      index: 0,
      messageId: getRowDataIdFromParents(node),
      timeSelected: direction === 'down',
    }
  }
  if (node.nodeType === Node.TEXT_NODE) {
    if (node.parentElement?.parentElement?.hasAttribute('data-log-message')) {
      // this is just a text node in message
      return {
        index: offset,
        messageId: getRowDataIdFromParents(node),
        timeSelected: direction === 'up',
      }
    }
    if (node.parentElement?.parentElement?.parentElement?.hasAttribute('data-log-message')) {
      // this is a text node in span with special formating
      let allNodesOffset = 0
      for (const el of node.parentElement?.parentElement.children) {
        if (el === node.parentElement) {
          return {
            index: allNodesOffset + offset,
            messageId: getRowDataIdFromParents(node),
            timeSelected: direction === 'up',
          }
        }
        if (el instanceof HTMLElement) {
          allNodesOffset += el.innerText.length
        }
      }
      return {
        index: 0,
        messageId: getRowDataIdFromParents(node),
        timeSelected: direction === 'up',
      }
    }
  }
  if (!(node instanceof HTMLElement)) {
    return null
  }
  if (node.hasAttribute('data-log-message')) {
    return {
      index: offset,
      messageId: getRowDataIdFromParents(node),
      timeSelected: direction === 'up',
    }
  }
  if (node.hasAttribute('data-log-row')) {
    let textNodeIndex: number | null = null
    node.childNodes.forEach((i, idx) => {
      if (i instanceof HTMLElement && i.hasAttribute('data-log-message')) {
        textNodeIndex = idx
      }
    })
    if (textNodeIndex != null) {
      return {
        messageId: getDataId(node),
        index: offset > textNodeIndex ? -1 : 0,
        timeSelected: offset === 0 ? direction === 'down' : direction === 'up',
      }
    }
  }
  const messageId = getRowDataIdFromParents(node)
  if (messageId != null) {
    // return a whole message if it is impossible to find accurate offset of text selection
    return {
      messageId,
      index: direction === 'down' ? 0 : -1,
      timeSelected: true,
    }
  }
  return null
}

export const getBuildLogMessageSelectionStart = (
  direction: 'down' | 'up',
): BuildLogMessageSelectionStart | null => {
  const selection = document.getSelection()
  if (selection == null || selection.isCollapsed || selection.anchorNode == null) {
    return null
  }
  const messageSelection = getMessageSelectionPosition(selection, direction)
  return messageSelection?.messageId != null
    ? {
        messageId: messageSelection.messageId,
        index: messageSelection.index,
        timeSelected: messageSelection.timeSelected,
        direction,
      }
    : null
}

function getMessageTextForCoping(message: BuildLogMessage): string {
  const text = getTextWithSpaceIndents(message.text, leftPad(message.level)).replace(/\u00a0/g, ' ')
  return message.containAnsi ? text.replace(new RegExp(ansiRegexpString, 'ig'), '') : text
}

export const getTextForCoping = ({
  selectionStart,
  messages,
  isAutodetectTimeZone,
}: {
  selectionStart: BuildLogMessageSelectionStart
  messages: ReadonlyArray<BuildLogMessage | null | undefined>
  isAutodetectTimeZone: boolean
}): string => {
  const selection = document.getSelection()
  if (
    selection == null ||
    selection.isCollapsed ||
    selection.focusNode == null ||
    messages.length === 0
  ) {
    return ''
  }

  const currentSelection = getMessageSelectionPosition(
    selection,
    selectionStart.direction === 'up' ? 'down' : 'up',
  )
  if (currentSelection?.messageId == null) {
    return ''
  }

  let startId: number
  let startIndex: number
  let startTimeSelected: boolean
  let endId: number
  let endIndex: number
  let endTimeSelected: boolean

  if (selectionStart.direction === 'down') {
    startId = selectionStart.messageId
    startIndex = selectionStart.index
    startTimeSelected = selectionStart.timeSelected
    endId = currentSelection.messageId
    endIndex = currentSelection.index
    endTimeSelected = currentSelection.timeSelected
  } else {
    startId = currentSelection.messageId
    startIndex = currentSelection.index
    startTimeSelected = currentSelection.timeSelected
    endId = selectionStart.messageId
    endIndex = selectionStart.index
    endTimeSelected = selectionStart.timeSelected
  }

  const copyMessages: Array<string> = []
  let i = 0
  let message: BuildLogMessage | null | undefined
  let insideSelection = false

  do {
    message = messages[i]
    i++
    if (message != null) {
      if (message.id === startId) {
        const text = getMessageTextForCoping(message)
        if (startTimeSelected) {
          copyMessages.push(printMessageTime(message.timestamp, isAutodetectTimeZone) + text)
        } else {
          copyMessages.push(
            startIndex >= 0 ? getMessageTextForCoping(message).slice(startIndex) : '',
          )
        }
        insideSelection = true
      } else if (message.id > startId && message.id < endId) {
        copyMessages.push(
          printMessageTime(message.timestamp, isAutodetectTimeZone) +
            getMessageTextForCoping(message),
        )
      } else if (message.id === endId) {
        const text = getMessageTextForCoping(message)
        if (endTimeSelected) {
          copyMessages.push(
            printMessageTime(message.timestamp, isAutodetectTimeZone) +
              (endIndex < 0 ? text : text.slice(0, endIndex)),
          )
        } else {
          copyMessages.push('')
        }
        insideSelection = false
      }
    } else if (insideSelection) {
      copyMessages.push('\n~~~~~~~~~~~~~~~~~~~~~\n\n')
    }
  } while (message == null || message.id < endId)

  return copyMessages.join('\n')
}
